
import { useState, useEffect, useCallback } from 'react';
import { ethers, EventLog, Result } from 'ethers';
import { Contract, ContractStatus, ContractType, User, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import ContractFactoryABI from '@/abis/ContractFactory.json';

const mockUser: User = {
  id: 'mock-user',
  name: 'Mock User',
  role: UserRole.GUEST,
  email: 'mock@user.com'
}

interface ApiContract {
    id: string;
    name: string;
    type: ContractType;
    status: ContractStatus;
    terms: string;
    initiator: User;
    initiatorId: string;
    parties: User[];
    createdAt: string;
    updatedAt: string;
}

export const useContracts = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { provider, isConnected } = useWallet();

  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    addLog("Starting contract data fetch.", LogLevel.INFO, LogCategory.DATA);

    try {
      let onChainContracts: Contract[] = [];
      let offChainContracts: Contract[] = [];

      if (provider && isConnected) {
        try {
          const contractFactory = new ethers.Contract(contractAddresses.ContractFactory, ContractFactoryABI.abi, provider);
          const contractCreatedEvents = await contractFactory.queryFilter(contractFactory.filters.ContractCreated());

          onChainContracts = (contractCreatedEvents as EventLog[]).map((event) => {
            const args = event.args as Result;
            return {
              id: args.contractAddress,
              name: args.contractType,
              type: args.contractType as ContractType,
              status: ContractStatus.ACTIVE,
              terms: 'On-chain contract',
              initiator: mockUser,
              initiatorId: mockUser.id,
              parties: [mockUser],
              createdAt: new Date(),
              updatedAt: new Date(),
            };
          });
          addLog(`Fetched ${onChainContracts.length} on-chain contracts.`, LogLevel.INFO, LogCategory.DATA);
        } catch (e: unknown) {
          const onChainError = "Could not load on-chain data. Check wallet connection or contract status.";
          addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
          setError(onChainError);
          notify(onChainError, 'warn');
        }
      }

      try {
        let apiData;
        const response = await fetch(`${API_BASE_URL}/api/contracts`, { credentials: 'include' });

        if (response.ok) {
          apiData = await response.json();
          addLog(`Fetched ${apiData.length} off-chain contracts from REAL API.`, LogLevel.INFO, LogCategory.API);
        } else {
          addLog(`Real API failed. Mock data for contracts is not available.`, LogLevel.WARN, LogCategory.API);
          apiData = []; // No mock data available
          notify('Displaying only on-chain data. Could not connect to the real API.', 'warn');
        }
        
        if (Array.isArray(apiData)) {
            offChainContracts = apiData.map((contract: ApiContract): Contract => ({
                ...contract,
                createdAt: new Date(contract.createdAt),
                updatedAt: new Date(contract.updatedAt),
            }));
        } else {
            throw new Error('Fetched off-chain data is not an array.');
        }

      } catch (e: unknown) {
        const offChainError = `Critical: Failed to load off-chain contract data: ${(e as Error).message}`;
        addLog(offChainError, LogLevel.CRITICAL, LogCategory.API);
        setError(prev => prev ? `${prev} | ${offChainError}` : offChainError);
        notify(offChainError, 'error');
      }
      
      const all = [...onChainContracts, ...offChainContracts].sort((a, b) => a.name.localeCompare(b.name));
      setContracts(all);

    } catch (e: unknown) {
      const genericError = `Failed to load contract data: ${(e as Error).message}`;
      setError(genericError);
      addLog(genericError, LogLevel.ERROR, LogCategory.DATA);
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return { contracts, isLoading, error, refetch: fetchContracts };
};
